import { useCallback } from 'react';

const WIZARD_STORAGE_KEY = 'clicklogos_wizard_state';
const OAUTH_PENDING_KEY = 'clicklogos_oauth_pending';
const AUTH_RETURN_PATH_KEY = 'clicklogos_auth_return_path';

// Max age in minutes before state is considered stale
const MAX_STATE_AGE_MINUTES = 30;

export interface WizardFormData {
  brandName: string;
  segment: string;
  description: string;
  personality: string;
  color: string;
  symbolConcept: string;
  conceptRefinement: string;
}

export interface GeneratedBrandData {
  success: boolean;
  logo: { url: string; transparentUrl?: string; prompt: string };
  fontKit: {
    display: { name: string; purpose: string; downloadLink?: string };
    titles: { name: string; purpose: string; downloadLink?: string };
    body: { name: string; purpose: string; downloadLink?: string };
  };
  colorPalette: Array<{ name: string; hex: string; role: string }>;
}

export interface WizardState {
  formData: WizardFormData;
  currentStep: number;
  generatedBrand: GeneratedBrandData | null;
  rawLogoUrl: string | null;
  generationCount: number;
  savedAt: number;
}

export const useWizardPersistence = () => {
  /**
   * Save wizard state to localStorage before OAuth redirect
   */
  const saveWizardState = useCallback((
    formData: WizardFormData,
    currentStep: number,
    generatedBrand: GeneratedBrandData | null,
    generationCount: number,
    rawLogoUrl: string | null = null
  ) => {
    const state: WizardState = {
      formData,
      currentStep,
      generatedBrand,
      rawLogoUrl,
      generationCount,
      savedAt: Date.now(),
    };
    
    try {
      localStorage.setItem(WIZARD_STORAGE_KEY, JSON.stringify(state));
      console.log('[WizardPersistence] State saved at step', currentStep, 'rawLogoUrl:', !!rawLogoUrl);
    } catch (error) {
      console.error('[WizardPersistence] Failed to save state:', error);
    }
  }, []);

  /**
   * Restore wizard state from localStorage
   * Returns null if no valid state exists or if state is too old
   */
  const restoreWizardState = useCallback((): WizardState | null => {
    try {
      const savedStateRaw = localStorage.getItem(WIZARD_STORAGE_KEY);
      
      if (!savedStateRaw) {
        return null;
      }
      
      const savedState: WizardState = JSON.parse(savedStateRaw);
      
      // Check if state is too old
      const ageInMinutes = (Date.now() - savedState.savedAt) / 1000 / 60;
      
      if (ageInMinutes > MAX_STATE_AGE_MINUTES) {
        console.log('[WizardPersistence] State too old:', ageInMinutes.toFixed(1), 'min');
        clearWizardState();
        return null;
      }
      
      // Validate state has required fields
      if (!savedState.formData || typeof savedState.currentStep !== 'number') {
        console.log('[WizardPersistence] Invalid state structure');
        clearWizardState();
        return null;
      }
      
      console.log('[WizardPersistence] Restoring state from', ageInMinutes.toFixed(1), 'min ago');
      return savedState;
    } catch (error) {
      console.error('[WizardPersistence] Failed to restore state:', error);
      clearWizardState();
      return null;
    }
  }, []);

  /**
   * Clear wizard state from localStorage
   */
  const clearWizardState = useCallback(() => {
    try {
      localStorage.removeItem(WIZARD_STORAGE_KEY);
      localStorage.removeItem(OAUTH_PENDING_KEY);
      console.log('[WizardPersistence] State cleared');
    } catch (error) {
      console.error('[WizardPersistence] Failed to clear state:', error);
    }
  }, []);

  /**
   * Mark that OAuth redirect is pending
   */
  const markOAuthPending = useCallback(() => {
    try {
      localStorage.setItem(OAUTH_PENDING_KEY, 'true');
      console.log('[WizardPersistence] OAuth pending flag set');
    } catch (error) {
      console.error('[WizardPersistence] Failed to set OAuth flag:', error);
    }
  }, []);

  /**
   * Check if OAuth redirect was pending (user is returning from OAuth)
   */
  const wasOAuthPending = useCallback((): boolean => {
    try {
      const pending = localStorage.getItem(OAUTH_PENDING_KEY);
      return pending === 'true';
    } catch (error) {
      return false;
    }
  }, []);

  /**
   * Clear OAuth pending flag
   */
  const clearOAuthPending = useCallback(() => {
    try {
      localStorage.removeItem(OAUTH_PENDING_KEY);
    } catch (error) {
      console.error('[WizardPersistence] Failed to clear OAuth flag:', error);
    }
  }, []);

  /**
   * Check if there's valid saved state (for UI indicators)
   */
  const hasSavedState = useCallback((): boolean => {
    try {
      const savedStateRaw = localStorage.getItem(WIZARD_STORAGE_KEY);
      if (!savedStateRaw) return false;
      
      const savedState: WizardState = JSON.parse(savedStateRaw);
      const ageInMinutes = (Date.now() - savedState.savedAt) / 1000 / 60;
      
      return ageInMinutes <= MAX_STATE_AGE_MINUTES;
    } catch {
      return false;
    }
  }, []);

  /**
   * Save the current path for return after OAuth
   */
  const saveReturnPath = useCallback((path: string) => {
    try {
      localStorage.setItem(AUTH_RETURN_PATH_KEY, path);
      console.log('[WizardPersistence] Return path saved:', path);
    } catch (error) {
      console.error('[WizardPersistence] Failed to save return path:', error);
    }
  }, []);

  /**
   * Get the saved return path
   */
  const getReturnPath = useCallback((): string | null => {
    try {
      return localStorage.getItem(AUTH_RETURN_PATH_KEY);
    } catch {
      return null;
    }
  }, []);

  /**
   * Clear the return path after use
   */
  const clearReturnPath = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_RETURN_PATH_KEY);
      console.log('[WizardPersistence] Return path cleared');
    } catch (error) {
      console.error('[WizardPersistence] Failed to clear return path:', error);
    }
  }, []);

  return {
    saveWizardState,
    restoreWizardState,
    clearWizardState,
    markOAuthPending,
    wasOAuthPending,
    clearOAuthPending,
    hasSavedState,
    saveReturnPath,
    getReturnPath,
    clearReturnPath,
  };
};
